<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  adminCreateEngineer,
  adminListDistricts,
  adminListEngineers,
  adminPatchEngineer,
  type Engineer,
  type EngineerRole,
} from '../api';

const ROLES: EngineerRole[] = ['выездной', 'менеджер', 'бухгалтер', 'админ'];

const engineers = ref<Engineer[]>([]);
const districts = ref<string[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const showAddForm = ref(false);
const onlyActive = ref(false);

// Add form
const newUserId = ref('');
const newRole = ref<EngineerRole>('выездной');
const newDistrict = ref<string>('');
const newFullName = ref('');

// Edit state (один на UI — простой подход)
const editingId = ref<string | null>(null);
const editRole = ref<EngineerRole>('выездной');
const editOriginalRole = ref<EngineerRole>('выездной');
const editDistrict = ref<string>('');
const editFullName = ref('');
const editActive = ref(true);

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const [eng, dist] = await Promise.all([
      adminListEngineers(onlyActive.value),
      adminListDistricts(),
    ]);
    engineers.value = eng;
    districts.value = dist;
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);

async function submitAdd() {
  if (!newUserId.value.trim()) {
    error.value = 'max_user_id обязателен';
    return;
  }
  try {
    await adminCreateEngineer({
      max_user_id: newUserId.value.trim(),
      role: newRole.value,
      active: true,
      district: newDistrict.value || null,
      full_name_override: newFullName.value.trim() || null,
    });
    showAddForm.value = false;
    newUserId.value = '';
    newDistrict.value = '';
    newFullName.value = '';
    await load();
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}

function startEdit(e: Engineer) {
  editingId.value = e.max_user_id;
  editRole.value = e.role;
  editOriginalRole.value = e.role;
  editDistrict.value = e.district ?? '';
  editFullName.value = e.full_name_override ?? '';
  editActive.value = e.active;
}

function cancelEdit() {
  editingId.value = null;
}

async function submitEdit(maxUserId: string) {
  // TASK-030 D: повышение до админа даёт полный доступ (whitelist, аудит, OCR) —
  // требуем явное подтверждение, чтобы не назначить админом случайно.
  if (editRole.value === 'админ' && editOriginalRole.value !== 'админ') {
    const ok = confirm(
      `Сделать пользователя ${maxUserId} АДМИНОМ?\n\n` +
        'Админ получает полные права: управление людьми, аудит, OCR, ' +
        'удаление договоров. Подтвердите передачу прав.',
    );
    if (!ok) return;
  }
  try {
    await adminPatchEngineer(maxUserId, {
      role: editRole.value,
      active: editActive.value,
      district: editDistrict.value || undefined,
      clear_district: !editDistrict.value,
      full_name_override: editFullName.value.trim() || undefined,
      clear_full_name_override: !editFullName.value.trim(),
    });
    editingId.value = null;
    await load();
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}

async function toggleActive(e: Engineer) {
  try {
    await adminPatchEngineer(e.max_user_id, { active: !e.active });
    await load();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  }
}
</script>

<template>
  <section class="flex flex-col gap-3 p-2 admin-tab-content">
    <header class="flex items-center justify-between">
      <h2 class="text-lg font-semibold">Пользователи</h2>
      <button class="text-sm text-blue-600" @click="showAddForm = !showAddForm">
        {{ showAddForm ? '× Отмена' : '+ Добавить' }}
      </button>
    </header>

    <label class="flex items-center gap-2 text-sm">
      <input type="checkbox" v-model="onlyActive" @change="load" />
      Только активные
    </label>

    <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>

    <!-- Add form -->
    <div v-if="showAddForm" class="bg-secondary-bg p-3 rounded flex flex-col gap-2">
      <h3 class="font-medium">Новый пользователь</h3>
      <input
        v-model="newUserId"
        type="text"
        inputmode="numeric"
        placeholder="ID пользователя MAX (например 118458975)"
        class="input"
      />
      <select v-model="newRole" class="input">
        <option v-for="r in ROLES" :key="r" :value="r">{{ r }}</option>
      </select>
      <select v-model="newDistrict" class="input">
        <option value="">— без района —</option>
        <option v-for="d in districts" :key="d" :value="d">{{ d }}</option>
      </select>
      <input
        v-model="newFullName"
        type="text"
        placeholder="ФИО для отображения (если не указано — возьмётся из MAX)"
        class="input"
      />
      <button class="btn" @click="submitAdd">Создать</button>
    </div>

    <p v-if="loading" class="text-hint text-sm">Загрузка…</p>

    <!-- List -->
    <ul class="flex flex-col gap-2">
      <li
        v-for="e in engineers"
        :key="e.max_user_id"
        class="border rounded p-2 flex flex-col gap-2"
        :class="{ 'opacity-50': !e.active }"
      >
        <div v-if="editingId !== e.max_user_id" class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <span class="font-mono text-sm">{{ e.max_user_id }}</span>
            <span class="text-xs rounded px-2 py-0.5 bg-secondary-bg">
              {{ e.role }}{{ e.active ? '' : ' (выкл)' }}
            </span>
          </div>
          <p class="text-sm">{{ e.full_name_override || '—' }}</p>
          <p class="text-xs text-hint">
            район: {{ e.district || '—' }}
          </p>
          <div class="flex gap-2 mt-1">
            <button class="text-sm text-blue-600" @click="startEdit(e)">
              Изменить
            </button>
            <button
              class="text-sm"
              :class="e.active ? 'text-orange-600' : 'text-green-600'"
              @click="toggleActive(e)"
            >
              {{ e.active ? 'Деактивировать' : 'Активировать' }}
            </button>
          </div>
        </div>

        <div v-else class="flex flex-col gap-2">
          <label class="text-xs text-hint">Роль</label>
          <select v-model="editRole" class="input">
            <option v-for="r in ROLES" :key="r" :value="r">{{ r }}</option>
          </select>
          <label class="text-xs text-hint">Район</label>
          <select v-model="editDistrict" class="input">
            <option value="">— без района —</option>
            <option v-for="d in districts" :key="d" :value="d">{{ d }}</option>
          </select>
          <label class="text-xs text-hint">ФИО override</label>
          <input v-model="editFullName" type="text" class="input" />
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" v-model="editActive" />
            Активен
          </label>
          <div class="flex gap-2">
            <button class="btn flex-1" @click="submitEdit(e.max_user_id)">
              Сохранить
            </button>
            <button class="btn btn-secondary flex-1" @click="cancelEdit">
              Отмена
            </button>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>
